import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import http from '../../api/http';
import {
  FiHeart,
  FiShoppingBag,
  FiSearch,
  FiLogIn,
  FiUser,
  FiLogOut,
  FiMenu,
  FiX,
  FiGrid,
} from 'react-icons/fi';
import './Header.css';

export default function Header({ onCartClick }) {
  const { user, loading, logout } = useAuth();
  const { totalQty } = useCart();
  const { count: favCount } = useFavorites();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const overHero = pathname === '/';

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 900 && menuOpen) setMenuOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  return (
    <header className={`hdr${overHero ? ' hdr--over-hero' : ''}`}>
      <div className="container">
        <div className="hdr__bar">
          <Link to="/" className="hdr__brand" aria-label="На главную">
            <img
              className="hdr__brand-img"
              src="/logo2.png"
              alt="LavSan"
              width="180"
              height="80"
              decoding="async"
            />
            <video
              className="hdr__brand-video"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster="/logo2.png"
              aria-hidden="true"
            >
              <source src="/logo-brand.webm" type="video/webm" />
              <source src="/logo-brand.mp4" type="video/mp4" />
            </video>
          </Link>

          <SearchBox />

          <div className="hdr__actions">
            <NavLink to="/catalog" className="hdr__pill hide-mobile">
              <FiGrid />
              <span>Каталог</span>
            </NavLink>

            {loading ? null : user ? (
              <NavLink to="/account" className="hdr__pill hide-mobile" title="Личный кабинет">
                <FiUser />
                <span>Кабинет</span>
              </NavLink>
            ) : (
              <Link to="/auth" className="hdr__pill hide-mobile">
                <FiLogIn />
                <span>Войти</span>
              </Link>
            )}

            {(user?.role === 'admin' || user?.role === 'manager') && (
              <NavLink to="/admin" className="hdr__pill hide-mobile">
                Админ
              </NavLink>
            )}

            <Link
              to="/wishlist"
              className={`hdr__circle ${favCount > 0 ? 'is-hot' : ''}`}
              aria-label="Избранное"
            >
              <FiHeart />
              {favCount > 0 && <span className="hdr__badge">{favCount}</span>}
            </Link>

            <button
              type="button"
              className={`hdr__circle hdr__circle--cart ${totalQty > 0 ? 'has-items' : ''}`}
              aria-label="Открыть корзину"
              onClick={onCartClick}
            >
              <FiShoppingBag />
              {totalQty > 0 && <span className="hdr__badge">{totalQty}</span>}
            </button>

            {user ? (
              <button
                type="button"
                className="hdr__circle hide-mobile"
                aria-label="Выйти"
                onClick={logout}
                title="Выйти"
              >
                <FiLogOut />
              </button>
            ) : null}

            <button
              type="button"
              className="hdr__circle hdr__burger"
              aria-label="Открыть меню"
              aria-controls="mnav"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <FiMenu />
            </button>
          </div>
        </div>
      </div>

      <MobileNav
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        user={user}
        loading={loading}
        logout={logout}
      />
    </header>
  );
}

function MobileNav({ open, onClose, user, loading, logout }) {
  return (
    <>
      <div
        className={`mnav__backdrop ${open ? 'is-open' : ''}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        id="mnav"
        className={`mnav ${open ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Навигация"
      >
        <div className="mnav__head">
          <span>Меню</span>
          <button type="button" className="mnav__close" aria-label="Закрыть меню" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <nav className="mnav__list">
          <NavLink to="/catalog" onClick={onClose} className="mnav__link">
            Каталог
          </NavLink>

          {loading ? (
            <span className="mnav__muted">Загрузка…</span>
          ) : user ? (
            <>
              <NavLink to="/account" onClick={onClose} className="mnav__link">
                <FiUser /> Личный кабинет
              </NavLink>
              {(user.role === 'admin' || user.role === 'manager') && (
                <NavLink to="/admin" onClick={onClose} className="mnav__link">
                  Админ
                </NavLink>
              )}
              <button
                type="button"
                className="mnav__btn"
                onClick={() => {
                  onClose();
                  logout();
                }}
              >
                <FiLogOut /> Выйти
              </button>
            </>
          ) : (
            <NavLink to="/auth" onClick={onClose} className="mnav__btn">
              <FiLogIn /> Войти
            </NavLink>
          )}
        </nav>
      </aside>
    </>
  );
}

function SearchBox() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [res, setRes] = useState([]);
  const nav = useNavigate();
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const phrases = useRef([
    'унитаз Grohe',
    'смеситель Gappo',
    'раковина Roca',
    'душевая система Hansgrohe',
    'сифон AlcaPlast',
  ]).current;

  const [ghostIndex, setGhostIndex] = useState(0);
  const [ghostLen, setGhostLen] = useState(0);
  const [ghostDir, setGhostDir] = useState('type');

  const elevated = focused || open;

  useEffect(() => {
    const current = phrases[ghostIndex];
    if (ghostDir === 'type') {
      if (ghostLen < current.length) {
        const id = setTimeout(() => setGhostLen(ghostLen + 1), 70);
        return () => clearTimeout(id);
      }
      const id = setTimeout(() => setGhostDir('erase'), 900);
      return () => clearTimeout(id);
    }
    if (ghostLen > 0) {
      const id = setTimeout(() => setGhostLen(ghostLen - 1), 40);
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => {
      setGhostIndex((ghostIndex + 1) % phrases.length);
      setGhostDir('type');
    }, 250);
    return () => clearTimeout(id);
  }, [ghostLen, ghostDir, ghostIndex, phrases]);

  useEffect(() => {
    if (!q.trim()) {
      setRes([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await http.get('/api/products', { params: { q, limit: 8 } });
        setRes(Array.isArray(data?.items) ? data.items : []);
        setOpen(true);
      } catch {
        /* ignore */
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) {
        setOpen(false);
        setFocused(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setFocused(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (!elevated) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [elevated]);

  const ghostText = phrases[ghostIndex].slice(0, ghostLen);

  const closeSearch = () => {
    setOpen(false);
    setFocused(false);
    inputRef.current?.blur();
  };

  const submitSearch = () => {
    const query = q.trim();
    if (res.length) {
      nav(`/p/${res[0].slug}`);
      closeSearch();
      return;
    }
    if (query) {
      nav(`/catalog?q=${encodeURIComponent(query)}`);
      closeSearch();
      return;
    }
    nav('/catalog');
    closeSearch();
  };

  return (
    <>
      <div
        className={`hdr__search-veil${elevated ? ' is-on' : ''}`}
        aria-hidden={!elevated}
        onMouseDown={(e) => {
          e.preventDefault();
          closeSearch();
        }}
      />
      <div className={`hdr__search${elevated ? ' is-elevated' : ''}`} ref={boxRef}>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          placeholder=""
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => {
            setFocused(true);
            if (res.length) setOpen(true);
          }}
          onBlur={() => {
            // даём кликнуть по подсказке до закрытия
            window.setTimeout(() => {
              if (!boxRef.current?.contains(document.activeElement)) {
                setFocused(false);
                setOpen(false);
              }
            }, 0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submitSearch();
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              closeSearch();
            }
          }}
          aria-label="Поиск по названию"
          aria-expanded={open && res.length > 0}
          aria-autocomplete="list"
          aria-controls="hdr-search-suggest"
        />
        {q === '' && (
          <span className="hdr__search-ghost" aria-hidden>
            {ghostText}
            <i className="caret" />
          </span>
        )}

        <button type="button" className="hdr__search-btn" onClick={submitSearch}>
          <span>Найти</span>
          <FiSearch />
        </button>

        {open && res.length > 0 && (
          <div id="hdr-search-suggest" className="hdr__suggest" role="listbox">
            {res.map((item) => {
              const img = item.images?.[0]?.url || '';
              const price = item.price?.effective ?? item.priceRetail ?? 0;
              return (
                <Link
                  key={item._id || item.slug}
                  to={`/p/${item.slug}`}
                  className="hdr__sug-item"
                  onClick={closeSearch}
                >
                  {img ? <img src={img} alt="" /> : <div className="hdr__sug-ph" />}
                  <div className="hdr__sug-text">
                    <div className="hdr__sug-title">{item.title}</div>
                    <div className="hdr__sug-price">{price.toLocaleString('ru-RU')} ₽</div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
